// Updated Login_Form.tsx - Redesigned Mobile Layout to Match RecommendedPlanDetails
// Key changes: Removed mobile logo, added top padding, improved spacing, fixed button at bottom

import React, { useState } from "react";
import { ReactComponent as Logo } from "../../../assets/icons/svg/triva_logo.svg";
import { ReactComponent as GoogleIcon } from "../../../assets/icons/svg/google_icon.svg";
import { ReactComponent as AppleIcon } from "../../../assets/icons/svg/apple_icon.svg";
import { ReactComponent as MailIcon } from "../../../assets/icons/svg/Mail_icon.svg";
import { ReactComponent as PhoneIcon } from "../../../assets/icons/svg/Phone_icon.svg";
import { ArrowLeft, ChevronLeft } from "lucide-react";
import { API_URLS } from "../../api/urls";

import { AuthStorage } from "./AuthStorage";
import { useGoogleAuth } from "../../../hooks/useGoogleAuth";
import { GoogleAuthService } from "../../../services/googleAuthService";

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface LoginFormProps {
  onBack?: () => void;
  onLoginSuccess?: (userData?: any) => void;
  onOTPRequired?: (loginData: LoginResponseData) => void;
  onGoogleLoginSuccess?: (userData: any, needsRegistration: boolean) => void;
  showBackButton?: boolean;
  isGoogleProcessing?: boolean;
  onGoogleProcessingStart?: () => void;
}

interface LoginResponseData {
  Success: string;
  message: string;
  user_id: number;
  otp?: number;
  random_gen_no?: string;
  user_status: string;
  remaining_days: number;
  access_token: string;
  refresh_token: string;
  contactInfo: string;
  contactType: "phone" | "email";
  isNewUser?: boolean;
  isGoogleAuthenticated?: boolean;
  user_data?: any;
}

const countries: Country[] = [
  { code: "+91", name: "India", flag: "https://flagcdn.com/w40/in.png" },
  { code: "+61", name: "Australia", flag: "https://flagcdn.com/w40/au.png" },
  { code: "+1", name: "Canada", flag: "https://flagcdn.com/w40/ca.png" },
  { code: "+1", name: "USA", flag: "https://flagcdn.com/w40/us.png" },
];

const phoneValidationRules: Record<string, { length: number; pattern?: RegExp }> = {
  '91': { length: 10, pattern: /^[6-9]\d{9}$/ },
  '1': { length: 10, pattern: /^[2-9]\d{9}$/ },
  '61': { length: 9, pattern: /^[2-9]\d{8}$/ },
};

export const formatPhoneNumber = (
  phone: string, 
  countryCode: string, 
  format: 'api' | 'display' | 'international' = 'display'
): string => {
  const cleanPhone = phone.replace(/\D/g, '');
  const cleanCode = countryCode.replace('+', '');
  
  switch (format) {
    case 'api':
      return `${countryCode}${cleanPhone}`;
    case 'display':
      return `${countryCode} ${cleanPhone}`;
    case 'international':
      if (cleanCode === '91') {
        return `${countryCode} ${cleanPhone.slice(0, 5)} ${cleanPhone.slice(5)}`;
      } else if (cleanCode === '1') {
        return `${countryCode} (${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
      }
      return `${countryCode} ${cleanPhone}`;
    default:
      return `${countryCode} ${cleanPhone}`;
  }
};

const LoginForm: React.FC<LoginFormProps> = ({ 
  onBack, 
  onLoginSuccess, 
  onOTPRequired,
  onGoogleLoginSuccess,
  showBackButton = true,
  isGoogleProcessing = false,
  onGoogleProcessingStart
}) => {
  const [loginMode, setLoginMode] = useState<"phone" | "email">("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [internalGoogleProcessing, setInternalGoogleProcessing] = useState(false);

  const { signInWithGoogle, isLoading: isGoogleAuthLoading } = useGoogleAuth();
  const isGoogleLoading = isGoogleAuthLoading || internalGoogleProcessing || isGoogleProcessing;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string, countryCode: string): boolean => {
    const cleanCode = countryCode.replace('+', '');
    const rule = phoneValidationRules[cleanCode];
    
    if (!rule) return false;
    
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (cleanPhone.length !== rule.length) return false;
    
    if (rule.pattern && !rule.pattern.test(cleanPhone)) return false;
    
    return true;
  };

  const getFormattedPhoneForAPI = (): string => {
    return formatPhoneNumber(phoneNumber, selectedCountry.code, 'api');
  };

  const getDisplayPhoneNumber = (): string => {
    return formatPhoneNumber(phoneNumber, selectedCountry.code, 'display');
  };

  const handlePhoneChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    
    const cleanCode = selectedCountry.code.replace('+', '');
    const rule = phoneValidationRules[cleanCode];
    const expectedLength = rule ? rule.length : 10;
    
    const truncatedValue = numericValue.slice(0, expectedLength);
    setPhoneNumber(truncatedValue);
    
    if (truncatedValue.length > 0) {
      if (validatePhone(truncatedValue, selectedCountry.code)) {
        setPhoneError("");
      } else {
        if (cleanCode === '91' && truncatedValue.length > 0 && !/^[6-9]/.test(truncatedValue)) {
          setPhoneError("Indian mobile numbers must start with 6, 7, 8, or 9");
        } else if (cleanCode === '1' && truncatedValue.length > 0 && !/^[2-9]/.test(truncatedValue)) {
          setPhoneError("US/Canada numbers cannot start with 0 or 1");
        } else if (truncatedValue.length < expectedLength) {
          setPhoneError(`Phone number must be ${expectedLength} digits for ${selectedCountry.name}`);
        } else {
          setPhoneError(`Please enter a valid ${selectedCountry.name} phone number`);
        }
      }
    } else {
      setPhoneError("");
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    
    if (value.length > 0) {
      if (validateEmail(value)) {
        setEmailError("");
      } else {
        setEmailError("Please enter a valid email address");
      }
    } else {
      setEmailError("");
    }
  };

  const callUserLoginAPI = async () => {
    try {
      const loginData = loginMode === "phone" 
        ? {
            type: "mobileno",
            user_mobileno: getFormattedPhoneForAPI()
          }
        : {
            type: "email",
            email: email,
            is_google: false
          };

      console.log("Calling USER_LOGIN API with data:", loginData);

      const response = await fetch(API_URLS.USER_LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("USER_LOGIN API response:", result);

      if (result.Success === "1") {
        AuthStorage.setAuthData(
          result.access_token,
          result.refresh_token,
          result.user_id
        );

        const loginResponseData: LoginResponseData = {
          ...result,
          contactInfo: loginMode === "phone" 
            ? getDisplayPhoneNumber()
            : email,
          contactType: loginMode,
          otp: result.otp || undefined,
          isGoogleAuthenticated: false
        };

        if (onOTPRequired) {
          onOTPRequired(loginResponseData);
        }
      } else {
        throw new Error(result.message || "Login failed");
      }

    } catch (error: any) {
      console.error("Login API error:", error);
      setApiError(error.message || "Login failed. Please try again.");
    }
  };

  const handleSubmit = async () => {
    setApiError("");

    if (loginMode === "phone") {
      if (!validatePhone(phoneNumber, selectedCountry.code)) {
        const cleanCode = selectedCountry.code.replace('+', '');
        const rule = phoneValidationRules[cleanCode];
        const expectedLength = rule ? rule.length : 10;
        
        if (cleanCode === '91' && phoneNumber.length > 0 && !/^[6-9]/.test(phoneNumber)) {
          setPhoneError("Indian mobile numbers must start with 6, 7, 8, or 9");
        } else if (cleanCode === '1' && phoneNumber.length > 0 && !/^[2-9]/.test(phoneNumber)) {
          setPhoneError("US/Canada numbers cannot start with 0 or 1");
        } else {
          setPhoneError(`Phone number must be ${expectedLength} digits for ${selectedCountry.name}`);
        }
        return;
      }
    } else {
      if (!validateEmail(email)) {
        setEmailError("Please enter a valid email address");
        return;
      }
    }

    setIsLoading(true);
    
    try {
      await callUserLoginAPI();
    } catch (error) {
      console.error("Login submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setApiError("");
    setInternalGoogleProcessing(true);
    
    if (onGoogleProcessingStart) {
      onGoogleProcessingStart();
    }
    
    try {
      console.log("=== Starting Google Sign-in ===");
      
      const googleResult = await signInWithGoogle();
      
      if (!googleResult.success || !googleResult.user) {
        setApiError(googleResult.error || "Google sign-in failed");
        return;
      }

      const googleUser = googleResult.user;
      console.log("=== Google Sign-in Successful ===");
      console.log("Google User:", {
        email: googleUser.email,
        displayName: googleUser.displayName,
        uid: googleUser.uid
      });

      if (!GoogleAuthService.validateGoogleUser(googleUser)) {
        setApiError("Invalid Google account data. Please try again.");
        return;
      }

      console.log("=== Calling Backend API with Google Data ===");
      const loginResponse = await GoogleAuthService.loginWithGoogleUser(googleUser);
      
      console.log("=== Backend Login Response ===");
      console.log("Success:", loginResponse.Success);
      console.log("isGoogleAuthenticated:", loginResponse.isGoogleAuthenticated);
      console.log("Has OTP:", !!loginResponse.otp);
      console.log("User Status:", loginResponse.user_status);

      AuthStorage.setAuthData(
        loginResponse.access_token,
        loginResponse.refresh_token,
        loginResponse.user_id,
        loginResponse.user_data || null
      );

      const googleUserInfo = GoogleAuthService.extractUserInfo(googleUser);
      
      if (loginResponse.isGoogleAuthenticated) {
        console.log("=== Google User - Skipping OTP ===");
        await handleGoogleUserFlow(loginResponse, googleUser, googleUserInfo);
      } else if (loginResponse.otp) {
        console.warn("⚠️ Backend sent OTP for Google user - this shouldn't happen!");
        console.log("Routing to OTP verification as fallback");
        if (onOTPRequired) {
          onOTPRequired(loginResponse);
        }
      } else {
        console.log("=== No OTP Required ===");
        await handleGoogleUserFlow(loginResponse, googleUser, googleUserInfo);
      }

    } catch (error: any) {
      console.error("Google login process error:", error);
      setApiError(error.message || "Google login failed. Please try again.");
    } finally {
      setInternalGoogleProcessing(false);
    }
  };

  const handleGoogleUserFlow = async (
    loginResponse: LoginResponseData, 
    googleUser: any, 
    googleUserInfo: any
  ) => {
    try {
      console.log("=== Processing Google User Flow ===");
      console.log("Login Response:", loginResponse);
      console.log("Google User Info:", googleUserInfo);

      let needsNameUpdate = false;
      
      if (loginResponse.isNewUser) {
        if (googleUserInfo.firstName) {
          needsNameUpdate = true;
          console.log("New Google user - will update name");
        }
      } else if (loginResponse.user_data) {
        const userData = loginResponse.user_data;
        if (!userData.user_fname || userData.user_fname.trim() === '') {
          if (googleUserInfo.firstName) {
            needsNameUpdate = true;
            console.log("Existing user missing name - will update");
          }
        }
      }

      if (needsNameUpdate && googleUserInfo.firstName) {
        // Set name immediately so it's available when the next screen renders
        const updatedUserData = {
          ...(loginResponse.user_data || {}),
          user_fname: googleUserInfo.firstName,
          user_lname: googleUserInfo.lastName || '',
          user_id: loginResponse.user_id,
        };
        AuthStorage.setUserData(updatedUserData as any);

        // Persist to backend in the background
        GoogleAuthService.updateUserName(
          loginResponse.user_id,
          googleUserInfo.firstName,
          googleUserInfo.lastName || '',
          loginResponse.access_token
        ).catch((error) => {
          console.error("Failed to update name:", error);
        });
      }

      const hasCompleteName = (loginResponse.user_data?.user_fname && 
                              loginResponse.user_data.user_fname.trim() !== '') ||
                             (needsNameUpdate && googleUserInfo.firstName);

      console.log("=== Navigation Decision ===");
      console.log("Has Complete Name:", hasCompleteName);
      console.log("Needs Registration:", !hasCompleteName);

      if (onGoogleLoginSuccess) {
        onGoogleLoginSuccess(googleUserInfo, !hasCompleteName);
      } else if (onLoginSuccess) {
        onLoginSuccess(googleUserInfo);
      }

    } catch (error: any) {
      console.error("Google user flow error:", error);
      setApiError("Failed to process Google login. Please try again.");
    }
  };

  const handleSocialLogin = async (provider: string) => {
    if (provider === "google") {
      await handleGoogleLogin();
      return;
    }

    console.log(`Login with ${provider}`);
    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (error) {
      console.error(`${provider} login failed:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  // handleCountrySelect and getPhoneMaxLength commented out – country dropdown disabled (India only)
  /*
  const handleCountrySelect = (country: Country) => { ... };
  const getPhoneMaxLength = (): number => { ... };
  */

  const isFormValid = loginMode === "phone"
    ? phoneNumber.trim() && !phoneError && validatePhone(phoneNumber, selectedCountry.code)
    : email.trim() && !emailError && validateEmail(email);

  const isAnyLoading = isLoading || isGoogleLoading;

  return (
    <div className="min-h-screen bg-white md:min-h-0 flex flex-col md:block">
      {/* Mobile: Back button and title */}
      <div className="md:hidden mb-4">
       

        <div className="mb-12">
          <Logo className="h-10" />
        </div>

        <div>
          <h1 className="text-[32px] font-bold text-[#282828] mb-2 leading-tight">Login</h1>
          <p className="text-[17px] text-[#6D6D6D]">Login to your account</p>
        </div>
      </div>

      {/* Desktop: Back Button */}
      {showBackButton && onBack && (
        <div className="hidden md:block mb-6">
          <button
            onClick={onBack}
            disabled={isAnyLoading}
            className="flex items-center text-[#0DB5B5] hover:text-[#0A9999] transition-colors disabled:opacity-50"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
      )}

      {/* Main Content Wrapper */}
      <div className="flex-1 md:flex-none flex flex-col md:block  md:px-0 pb-[100px] md:pb-0">
        <div className="flex-1 md:flex-none bg-white md:rounded-[18px] rounded-none md:shadow-sm md:border-[0.5px] md:border-[#F2F2F2] p-0 md:p-8 flex flex-col md:pb-0">
          {/* Desktop: Logo + Title inside card */}
          <div className="hidden md:flex md:flex-col md:items-center text-center mb-6">
            <Logo className="h-10 mb-4" />
            <h1 className="text-[28px] 3xl:text-[32px] font-bold font-inter text-[#282828] mb-1">
              Login
            </h1>
            <p className="text-[16px] 3xl:text-[18px] font-medium font-inter text-[#6D6D6D]">
              Login to your account
            </p>
          </div>

          {/* Form Content */}
          <div className="space-y-5 flex-1 md:flex-none flex flex-col">
            {apiError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            )}

            <div>
              <label className="block text-[15px] md:text-sm 3xl:text-[14px] text-[#202020] mb-3 md:mb-2 font-normal">
                {loginMode === "phone"
                  ? "Enter your phone number"
                  : "Enter your email address"}
              </label>

              {loginMode === "phone" ? (
                <div>
                     {/* Mobile: Country selector inside input */}
                  <div className="md:hidden relative">
                    {/* Country flag – India only (dropdown disabled) */}
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
                      <img
                        src={countries[0].flag}
                        alt={countries[0].name}
                        className="w-7 h-5 rounded-sm object-cover"
                      />
                      {/* <svg chevron dropdown arrow commented out> */}
                    </div>

                    {/* Vertical separator */}
                    <div className="absolute left-[56px] top-1/2 -translate-y-1/2 h-6 w-[1px] bg-[#CECECE] z-10"></div>

                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      disabled={isAnyLoading}
                      className={`w-full pl-[68px] pr-4 py-[14px] border-2 rounded-[14px] focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed text-[16px] ${
                        phoneError
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#E5E5E5] focus:border-[#ABECEB]'
                      }`}
                      placeholder="9876543210"
                      maxLength={10}
                    />

                    {/* Country dropdown commented out – India only
                    {isDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-20" onClick={() => setIsDropdownOpen(false)} />
                        <div className="absolute top-full left-0 mt-2 w-full bg-white border-2 border-[#E5E5E5] rounded-[14px] shadow-lg z-30 max-h-60 overflow-y-auto">
                          {countries.map((country, index) => (
                            <button key={index} type="button" onClick={() => handleCountrySelect(country)}
                              className="w-full flex items-center px-4 py-3 text-left hover:bg-gray-50 first:rounded-t-[14px] last:rounded-b-[14px] focus:outline-none focus:bg-gray-50">
                              <img src={country.flag} alt={country.name} className="w-7 h-5 mr-3 rounded-sm object-cover" />
                              <div className="flex flex-col">
                                <span className="text-[15px] font-medium text-gray-900">{country.name}</span>
                                <span className="text-[13px] text-gray-500">{country.code}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    */}
                  </div>

                  {/* Desktop: Separate country selector */}
                  <div className="hidden md:flex">
                    {/* Country flag – India only (dropdown disabled) */}
                    <div className="flex items-center px-3 py-2 border border-[#ABECEB] rounded-[12px] bg-white mr-2">
                      <img
                        src={countries[0].flag}
                        alt={countries[0].name}
                        className="w-8 h-6 rounded-sm object-cover"
                      />
                      {/* Dropdown button commented out – India only
                      <svg chevron arrow />
                      */}
                    </div>

                    {/* Country dropdown list commented out – India only
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-[#ABECEB] rounded-[12px] shadow-lg z-10">
                        {countries.map((country, index) => (
                          <button key={index} type="button" onClick={() => handleCountrySelect(country)}
                            className="w-full flex items-center px-3 py-2 text-left hover:bg-gray-50 first:rounded-t-[12px] last:rounded-b-[12px] focus:outline-none focus:bg-gray-50">
                            <img src={country.flag} alt={country.name} className="w-5 h-4 mr-3 rounded-sm object-cover" />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900">{country.name}</span>
                              <span className="text-xs text-gray-500">{country.code}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    */}

                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      disabled={isAnyLoading}
                      className={`flex-1 px-3 py-2 border rounded-[12px] focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                        phoneError
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#ABECEB] focus:border-[#ABECEB]'
                      }`}
                      placeholder="9876543210"
                      maxLength={10}
                    />
                  </div>
                  {phoneError && (
                    <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                  )}
                </div>
              ) : (
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    disabled={isAnyLoading}
                    className={`w-full px-4 md:px-3 py-[14px] md:py-2 border-2 md:border rounded-[14px] md:rounded-[12px] focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed text-[16px] md:text-base ${
                      emailError 
                        ? 'border-red-500 focus:border-red-500' 
                        : 'border-[#E5E5E5] md:border-[#ABECEB] focus:border-[#ABECEB]'
                    }`}
                    placeholder="example@email.com"
                  />
                  {emailError && (
                    <p className="text-red-500 text-xs mt-1">{emailError}</p>
                  )}
                </div>
              )}
            </div>

            {/* Desktop: Submit button */}
            <button
              onClick={handleSubmit}
              disabled={!isFormValid || isAnyLoading}
              className={`hidden md:block w-full text-[16px] 3xl:text-[18px] font-medium py-3 px-4 rounded-[8px] transition-all ${
                !isFormValid || isAnyLoading
                  ? 'bg-[#F1FBFB] text-[#00A9A9] opacity-50 cursor-not-allowed'
                  : 'bg-[#06B4B4] text-white hover:bg-[#059999]'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Processing...
                </div>
              ) : (
                'Proceed for OTP'
              )}
            </button>

            {/* Desktop only: Terms */}
            <div className="hidden md:block text-center text-[12px] 3xl:text-sm font-inter text-[#262B36]">
              By signing in, you agree to our{" "}
              <a href="#" className="text-[#00A9A9] hover:underline">
                Terms of Service
              </a>
              {" & "}
              <a href="#" className="text-[#00A9A9] hover:underline">
                Privacy Policy
              </a>
            </div>

            {/* Mobile: OR divider */}
            <div className="md:hidden my-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">Or</span>
                </div>
              </div>
            </div>

            {/* Mobile: Social login buttons */}
            <div className="md:hidden space-y-3">
              <button
                onClick={() => handleSocialLogin("google")}
                disabled={isAnyLoading}
                className="w-full flex items-center justify-center px-4 py-3 border border-[#ABECEB] rounded-xl text-[#202020] text-[14px] font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGoogleLoading ? (
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-3"></div>
                ) : (
                  <GoogleIcon className="w-5 h-5 mr-3" />
                )}
                {isGoogleLoading ? 'Signing in with Google...' : 'Login via google'}
              </button>

              {/* <button
                onClick={() => handleSocialLogin("apple")}
                disabled={isAnyLoading}
                className="w-full flex items-center justify-center px-4 py-3 border border-[#ABECEB] rounded-xl text-[#202020] text-[14px] font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <AppleIcon className="w-5 h-5 mr-3" />
                Login via apple
              </button> */}

              <button
                onClick={() => {
                  setLoginMode(loginMode === "phone" ? "email" : "phone");
                  setPhoneError("");
                  setEmailError("");
                  setApiError("");
                }}
                disabled={isAnyLoading}
                className="w-full flex items-center justify-center px-4 py-3 border border-[#ABECEB] rounded-xl text-[#202020] text-[14px] font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loginMode === "phone" ? (
                  <>
                    <MailIcon className="w-5 h-5 mr-3" />
                    Login via email
                  </>
                ) : (
                  <>
                    <PhoneIcon className="w-5 h-5 mr-3" />
                    Login via phone number
                  </>
                )}
              </button>
            </div>

            {/* Mobile: Spacer to push button to bottom */}
            <div className="md:hidden flex-1"></div>
          </div>

          {/* Desktop: OR divider */}
          <div className="hidden md:block my-5">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t mx-16 border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">OR</span>
              </div>
            </div>
          </div>

          {/* Desktop: Social login buttons */}
          <div className="hidden md:block space-y-3 mb-10">
            <button
              onClick={() => handleSocialLogin("google")}
              disabled={isAnyLoading}
              className="w-full flex items-center justify-center px-4 py-3 border border-[#ABECEB] rounded-[8px] text-[#202020] text-[14px] 3xl:text-[16px] font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGoogleLoading ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-3"></div>
              ) : (
                <GoogleIcon className="w-5 h-5 mr-3" />
              )}
              {isGoogleLoading ? 'Signing in with Google...' : 'Login via google'}
            </button>

            <button
              onClick={() => {
                setLoginMode(loginMode === "phone" ? "email" : "phone");
                setPhoneError("");
                setEmailError("");
                setApiError("");
              }}
              disabled={isAnyLoading}
              className="w-full flex items-center justify-center px-4 py-3 border border-[#ABECEB] rounded-[8px] text-[#202020] text-[14px] 3xl:text-[16px] font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loginMode === "phone" ? (
                <>
                  <MailIcon className="w-5 h-5 mr-3" />
                  Login via email
                </>
              ) : (
                <>
                  <PhoneIcon className="w-5 h-5 mr-3" />
                  Login via phone number
                </>
              )}
            </button>
          </div>
        </div>

        {/* Mobile: Fixed bottom button - matching RecommendedPlanDetails */}
        <div className="md:hidden fixed -bottom-0.5 left-0 right-0 p-4" style={{ backdropFilter: "blur(10px)" }}>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid || isAnyLoading}
            className={`w-full py-4 rounded-xl font-medium text-[16px] transition-colors duration-200 ${
              !isFormValid || isAnyLoading
                ? 'bg-[#F1FBFB] text-[#00A9A9] opacity-50 cursor-not-allowed'
                : 'bg-[#0DB5B5] text-white hover:bg-[#0A9999]'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Processing...
              </div>
            ) : (
              'Proceed for OTP'
            )}
          </button>
        </div>
      </div>

      {/* Dropdown overlay */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-10 md:z-0"
          onClick={() => setIsDropdownOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default LoginForm;